/**
 * Test Data Generator — pure core (doc 147 §6.6.6).
 *
 * Given an `Entity` from the manifest, produce N plausible Firestore-shaped
 * sample documents that respect declared field types, optionality, FK
 * targets, and enum membership. Used by the Test Data Generator view to
 * produce Playwright fixtures + emulator seed scripts.
 *
 * Seeded so the same (entity, seed) always renders identical output —
 * essential for reproducible snapshots and CI fixtures.
 */
import type { AtlasManifest, Entity, Field } from '@atlas-manifest/types';

export interface GenerateOptions {
  seed: number;
  /** When `'sometimes'` (default), optional fields appear ~50% of the time. */
  includeOptional?: 'always' | 'sometimes' | 'never';
}

export interface GenerateSamplesOptions extends GenerateOptions {
  count: number;
}

/** Per-document max — keeps the UI responsive and clipboard-safe. */
export const MAX_SAMPLES = 500;

type Json = string | number | boolean | null | Json[] | { [key: string]: Json };
type Sample = Record<string, Json>;

/* ─── deterministic PRNG (mulberry32) ─────────────────────────────────── */

function makeRng(seed: number): () => number {
  let s = (seed | 0) >>> 0 || 1;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function pick<T>(rng: () => number, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)]!;
}

function randInt(rng: () => number, min: number, max: number): number {
  return Math.floor(rng() * (max - min + 1)) + min;
}

/* ─── locale tables (kept tiny on purpose) ────────────────────────────── */

const FIRST_NAMES = [
  'Alex', 'Jordan', 'Sam', 'Taylor', 'Morgan', 'Riley', 'Casey', 'Quinn',
  'Avery', 'Drew', 'Reese', 'Hayden', 'Skylar', 'Rowan', 'Sage', 'Phoenix',
] as const;
const LAST_NAMES = [
  'Reyes', 'Kim', 'Patel', 'Nguyen', 'Garcia', 'Brown', 'Martin', 'Walker',
  'Chen', 'Singh', 'Cohen', 'Diallo', 'Okafor', 'Sato', 'Rossi', 'Andersen',
] as const;
const CURRENCIES = ['USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'CHF'] as const;
const WORDS = [
  'alpha', 'bravo', 'cobalt', 'denim', 'ember', 'forest', 'glow', 'harbor',
  'indigo', 'jade', 'kestrel', 'lunar', 'mango', 'nova', 'orchid', 'plume',
] as const;

/* ─── per-field value generation ──────────────────────────────────────── */

function fieldHint(name: string): 'id' | 'email' | 'currency' | 'name' | 'phone' | null {
  const lc = name.toLowerCase();
  if (lc === 'currency') return 'currency';
  if (lc.endsWith('id') || lc.endsWith('ids') || lc === 'uid') return 'id';
  if (lc.includes('email')) return 'email';
  if (lc.includes('phone')) return 'phone';
  if (lc.endsWith('name')) return 'name';
  return null;
}

function makeId(rng: () => number, len = 12): string {
  const alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let out = '';
  for (let i = 0; i < len; i++) out += alphabet[Math.floor(rng() * alphabet.length)];
  return out;
}

function makeEmail(rng: () => number): string {
  const first = pick(rng, FIRST_NAMES).toLowerCase();
  const last = pick(rng, LAST_NAMES).toLowerCase();
  const domain = pick(rng, ['example.com', 'sample.org', 'mail.test']);
  return `${first}.${last}@${domain}`;
}

function makePhone(rng: () => number): string {
  return `+1${randInt(rng, 200, 999)}${randInt(rng, 200, 999)}${randInt(rng, 1000, 9999)}`;
}

function makeName(rng: () => number, field: string): string {
  if (field.toLowerCase().includes('last')) return pick(rng, LAST_NAMES);
  if (field.toLowerCase().includes('first')) return pick(rng, FIRST_NAMES);
  return `${pick(rng, FIRST_NAMES)} ${pick(rng, LAST_NAMES)}`;
}

function makeIsoDate(rng: () => number): string {
  // Random instant within the last ~year.
  const yearMs = 365 * 24 * 60 * 60 * 1000;
  const offset = Math.floor(rng() * yearMs);
  return new Date(Date.now() - offset).toISOString();
}

function makeMoney(rng: () => number): Sample {
  // Integer minor units so JSON stays clean.
  return { amount: randInt(rng, 1, 50_000), currency: pick(rng, CURRENCIES) };
}

function makeScalar(rng: () => number, field: Field): Json {
  const hint = fieldHint(field.name);
  if (hint === 'id') return makeId(rng);
  if (hint === 'email') return makeEmail(rng);
  if (hint === 'phone') return makePhone(rng);
  if (hint === 'currency') return pick(rng, CURRENCIES);
  if (hint === 'name') return makeName(rng, field.name);

  const t = field.type.replace(/\[\]$/, '').trim();
  if (t === 'string') return pick(rng, WORDS);
  if (t === 'number') return randInt(rng, 1, 9_999);
  if (t === 'boolean') return rng() < 0.5;
  if (t === 'Money') return makeMoney(rng);
  if (t === 'Timestamp' || t === 'Date' || /date|time/i.test(field.name)) {
    return makeIsoDate(rng);
  }
  // Enum / branded primitive — fall back to a word.
  if (field.referencesEnum) return pick(rng, WORDS).toUpperCase();
  if (field.referencesEntity) return makeId(rng);
  return pick(rng, WORDS);
}

function makeValue(rng: () => number, field: Field): Json {
  const isArray = /\[\]$/.test(field.type);
  if (isArray) {
    const n = randInt(rng, 1, 3);
    const arr: Json[] = [];
    for (let i = 0; i < n; i++) {
      arr.push(
        makeScalar(rng, { ...field, type: field.type.replace(/\[\]$/, '') }),
      );
    }
    return arr;
  }
  return makeScalar(rng, field);
}

/* ─── public API ──────────────────────────────────────────────────────── */

export function generateSample(
  entity: Entity,
  _manifest: AtlasManifest,
  opts: GenerateOptions,
): Sample {
  const rng = makeRng(opts.seed);
  const includeOptional = opts.includeOptional ?? 'sometimes';
  const out: Sample = {};
  for (const f of entity.fields) {
    if (f.optional) {
      if (includeOptional === 'never') continue;
      if (includeOptional === 'sometimes' && rng() < 0.5) continue;
    }
    out[f.name] = makeValue(rng, f);
  }
  return out;
}

export function generateSamples(
  entity: Entity,
  manifest: AtlasManifest,
  opts: GenerateSamplesOptions,
): Sample[] {
  const count = Math.max(0, Math.min(opts.count, MAX_SAMPLES));
  const out: Sample[] = [];
  for (let i = 0; i < count; i++) {
    out.push(generateSample(entity, manifest, { ...opts, seed: opts.seed + i }));
  }
  return out;
}

export function serializeJson(samples: readonly Sample[]): string {
  return JSON.stringify(samples, null, 2);
}

/**
 * Render samples as a TypeScript fixture literal:
 *   export const userFixtures = [...] as const;
 *
 * Single-quoted strings, bare keys where legal, `as const` for type narrowing
 * — matches the convention in `src/test/fixtures.ts`.
 */
export function serializeTs(samples: readonly Sample[], entityName: string): string {
  const ident =
    entityName.charAt(0).toLowerCase() + entityName.slice(1).replace(/[^A-Za-z0-9_$]/g, '');
  const body = samples.map((s) => renderTs(s, 2)).join(',\n');
  return (
    `// Generated for entity \`${entityName}\` — Atlas Test Data Generator (doc 147 §6.6.6).\n` +
    `export const ${ident}Fixtures = [\n${body}\n] as const;\n`
  );
}

const SAFE_KEY = /^[A-Za-z_$][A-Za-z0-9_$]*$/;

function renderTs(value: Json, indent: number): string {
  const pad = ' '.repeat(indent);
  const inner = ' '.repeat(indent + 2);

  if (value === null) return `${pad}null`;
  if (typeof value === 'boolean' || typeof value === 'number') return `${pad}${value}`;
  if (typeof value === 'string') return `${pad}${escapeString(value)}`;
  if (Array.isArray(value)) {
    if (value.length === 0) return `${pad}[]`;
    // Inline short arrays of primitives for readability.
    if (value.every((v) => typeof v !== 'object' || v === null) && value.length <= 6) {
      return `${pad}[${value.map((v) => renderTs(v, 0).trim()).join(', ')}]`;
    }
    return `${pad}[\n${value.map((v) => renderTs(v, indent + 2)).join(',\n')}\n${pad}]`;
  }
  // Object
  const keys = Object.keys(value);
  if (keys.length === 0) return `${pad}{}`;
  // Inline tiny scalar objects.
  if (
    keys.length <= 2 &&
    keys.every((k) => {
      const v = value[k]!;
      return typeof v !== 'object' || v === null;
    })
  ) {
    const body = keys
      .map((k) => `${renderKey(k)}: ${renderTs(value[k]!, 0).trim()}`)
      .join(', ');
    return `${pad}{ ${body} }`;
  }
  const body = keys
    .map((k) => `${inner}${renderKey(k)}: ${renderTs(value[k]!, indent + 2).trim()}`)
    .join(',\n');
  return `${pad}{\n${body}\n${pad}}`;
}

function renderKey(k: string): string {
  return SAFE_KEY.test(k) ? k : escapeString(k);
}

function escapeString(s: string): string {
  return `'${s.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}
